import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SellerConnection } from '../api/amazonconnect';
import { useGlobalFilter } from '../global-filter';
import { shell } from '../shell/shellClasses';
import { ActionToolbar } from './ActionToolbar';
import { DecisionBoard } from './DecisionBoard';
import { DecisionFilters } from './DecisionFiltersBar';
import {
  applyFilters,
  defaultFilters,
  groupByPriority,
  sortActions,
  uniqueOptions,
} from './filterActions';
import { loadCommandCenterActions, resolvePrimaryConnection } from './loadActions';
import {
  dismissAction,
  loadLocalFlags,
  markComplete,
  saveLocalFlags,
  toggleBookmark,
  togglePinned,
  unmarkComplete,
} from './localState';
import type {
  ActionPriority,
  BoardViewMode,
  CommandCenterModel,
  DecisionFiltersState,
  LocalActionFlags,
  SortMode,
} from './types';
import { EMPTY_BOARD_MESSAGE } from './types';

/**
 * Founder Command Center — Decision Board framework (COMMAND-CENTER-000001).
 * Connection / search shared via Global Filter Engine (GLOBAL-FILTER-ENGINE-000001).
 */
export function CommandCenterPage() {
  const { context: gfe, setFilters, buildLink } = useGlobalFilter();
  const [connection, setConnection] = useState<SellerConnection | null>(null);
  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [model, setModel] = useState<CommandCenterModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flags, setFlags] = useState<LocalActionFlags>(() => loadLocalFlags());
  const [view, setView] = useState<BoardViewMode>('active');
  const [sort, setSort] = useState<SortMode>('updated_desc');
  const [filters, setLocalFilters] = useState<DecisionFiltersState>(() => defaultFilters());
  const [expanded, setExpanded] = useState<Record<ActionPriority, boolean>>({
    critical: true,
    important: true,
    opportunity: true,
    information: false,
  });

  // Decision board search mirrors global search (business context)
  useEffect(() => {
    setLocalFilters((f) =>
      f.search === gfe.search ? f : { ...f, search: gfe.search },
    );
  }, [gfe.search]);

  const persist = useCallback((next: LocalActionFlags) => {
    setFlags(next);
    saveLocalFlags(next);
  }, []);

  const reload = useCallback(async (conn: SellerConnection | null) => {
    setRefreshing(true);
    setError(null);
    try {
      const m = await loadCommandCenterActions({ connection: conn });
      setModel(m);
      if (m.error) setError(m.error);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Command Center');
      setModel(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { connection: c, connections: list } = await resolvePrimaryConnection();
      if (cancelled) return;
      setConnections(list);
      const prefer =
        (gfe.connectionId && list.find((x) => x.id === gfe.connectionId)) || c;
      setConnection(prefer);
      if (prefer && gfe.connectionId !== prefer.id) {
        setFilters({
          connectionId: prefer.id,
          sellerLabel: prefer.seller_ref || gfe.sellerLabel,
        });
      }
      await reload(prefer);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once
  }, [reload]);

  // When GFE connection changes (e.g. user edits filter bar), reload board
  useEffect(() => {
    if (!connections.length || !gfe.connectionId) return;
    if (connection?.id === gfe.connectionId) return;
    const next = connections.find((c) => c.id === gfe.connectionId) ?? null;
    if (!next) return;
    setConnection(next);
    void reload(next);
  }, [gfe.connectionId, connections, connection?.id, reload]);

  const actions = model?.actions ?? [];
  const options = useMemo(
    () => uniqueOptions(actions.length ? actions : []),
    [actions],
  );

  const sellers = useMemo(() => {
    if (options.sellers.length) return options.sellers;
    return connections.map((c) => ({
      value: c.id,
      label: c.seller_ref || c.id,
    }));
  }, [options.sellers, connections]);

  const marketplaces = useMemo(() => {
    if (options.marketplaces.length) return options.marketplaces;
    const set = new Set<string>();
    for (const c of connections) {
      for (const m of c.marketplace_ids ?? []) set.add(m);
    }
    return [...set].sort();
  }, [options.marketplaces, connections]);

  const filtered = useMemo(() => {
    const withSearch = applyFilters(
      actions,
      { ...filters, search: filters.search },
      flags,
      view,
    );
    return sortActions(withSearch, sort, flags);
  }, [actions, filters, flags, view, sort]);

  const grouped = useMemo(() => groupByPriority(filtered), [filtered]);

  const onTogglePriority = (p: ActionPriority) => {
    setExpanded((prev) => ({ ...prev, [p]: !prev[p] }));
  };

  return (
    <div
      className="mx-auto w-full max-w-[100rem] space-y-4"
      data-program="COMMAND-CENTER-000001"
    >
      <header className="space-y-1">
        <p className={`text-[11px] font-medium uppercase tracking-wider ${shell.accent}`}>
          Founder Command Center
        </p>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-[var(--shell-fg)]">
              Decision Board
            </h1>
            <p className={`mt-1 max-w-2xl text-sm ${shell.muted}`}>
              What should I do next? Certified LIVE signals only — separate from Dashboard
              (&quot;What happened?&quot;). Framework only: no AI, no recommendation engine.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {connections.length > 0 && (
              <label className="flex flex-col gap-1 text-xs">
                <span className={shell.muted}>Seller connection</span>
                <select
                  className={`${shell.control} ${shell.focusRing} text-xs`}
                  value={connection?.id ?? ''}
                  onChange={(e) => {
                    const next =
                      connections.find((c) => c.id === e.target.value) ?? null;
                    setConnection(next);
                    setFilters({
                      connectionId: next?.id ?? null,
                      sellerLabel: next?.seller_ref || gfe.sellerLabel,
                    });
                    void reload(next);
                  }}
                >
                  {connections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.seller_ref || c.id}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Link
              to={buildLink('/dashboard')}
              className={`${shell.control} ${shell.focusRing} inline-flex min-h-11 items-center px-3 text-xs`}
            >
              Open Dashboard
            </Link>
          </div>
        </div>
      </header>

      <ActionToolbar
        view={view}
        sort={sort}
        onView={setView}
        onSort={setSort}
        onRefresh={() => void reload(connection)}
        refreshing={refreshing}
        resultCount={filtered.length}
        search={filters.search}
        onSearch={(q) => {
          setLocalFilters((f) => ({ ...f, search: q }));
          setFilters({ search: q });
        }}
      />

      <DecisionFilters
        filters={filters}
        onChange={setLocalFilters}
        workspaces={options.workspaces}
        sellers={sellers}
        marketplaces={marketplaces}
      />

      {loading && (
        <p className={`text-sm ${shell.muted}`} role="status">
          Loading decision board…
        </p>
      )}

      {error && (
        <div
          className="rounded-[var(--shell-radius-md)] border border-[var(--shell-danger)]/40 bg-[var(--shell-danger)]/10 px-4 py-3 text-sm text-[var(--shell-danger)]"
          role="alert"
        >
          {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div
          className={`rounded-[var(--shell-radius-lg)] border px-6 py-12 text-center ${shell.border} ${shell.surface}`}
          role="status"
        >
          <p className="text-sm font-medium text-[var(--shell-fg)]">{EMPTY_BOARD_MESSAGE}</p>
          <p className={`mt-2 text-xs ${shell.subtle}`}>
            {model?.sourceNote ??
              'Actions appear only when certified LIVE commerce rules produce signals.'}
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <DecisionBoard
          grouped={grouped}
          expanded={expanded}
          onTogglePriority={onTogglePriority}
          flags={flags}
          filterPriority={filters.priority}
          onPin={(id) => persist(togglePinned(flags, id))}
          onBookmark={(id) => persist(toggleBookmark(flags, id))}
          onComplete={(id) => persist(markComplete(flags, id))}
          onDismiss={(id) => persist(dismissAction(flags, id))}
          onRestore={(id) => persist(unmarkComplete(flags, id))}
        />
      )}

      {model?.truncated && (
        <p className={`text-xs ${shell.subtle}`} role="note">
          Source data was truncated — treat counts as partial until a fuller certified
          window is available.
        </p>
      )}

      <footer className={`border-t pt-3 text-[11px] ${shell.border} ${shell.subtle}`}>
        COMMAND-CENTER-000001 · GFE connection/search shared · local pin/bookmark ·{' '}
        {model?.loadedAt
          ? `loaded ${new Date(model.loadedAt).toLocaleString()}`
          : 'not loaded'}
      </footer>
    </div>
  );
}
